import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useAuth } from '../../context/AuthContext';
import jobService from '../../services/jobService';
import applicationService from '../../services/applicationService';
import './JobDetails.css';

const JobDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const inDashboard = isAuthenticated && user?.role === 'candidate';
  const [job, setJob] = useState(null);
  const [similarJobs, setSimilarJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSaved, setIsSaved] = useState(false);
  const [savingJob, setSavingJob] = useState(false);
  const [isApplied, setIsApplied] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchJobDetails();
    fetchSimilarJobs();
    if (isAuthenticated && user?.role === 'candidate') {
      checkIfSaved();
      checkIfApplied();
    }
  }, [id, isAuthenticated]);

  const fetchJobDetails = async () => {
    try {
      const response = await jobService.getJobById(id);
      setJob(response.data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching job:', error);
      toast.error('Failed to fetch job details');
      setLoading(false);
    }
  };

  const fetchSimilarJobs = async () => {
    try {
      const response = await jobService.getSimilarJobs(id);
      setSimilarJobs(response.data);
    } catch (error) {
      console.error('Error fetching similar jobs:', error);
    }
  };

  const handleApply = () => {
    if (!isAuthenticated) {
      toast.info('Please login to apply for this job');
      navigate('/login', { state: { from: `/jobs/${id}` } });
      return;
    }

    if (user.role !== 'candidate') {
      toast.error('Only candidates can apply for jobs');
      return;
    }

    // Navigate to application page
    navigate(`/jobs/${id}/apply`);
  };

  const checkIfSaved = async () => {
    try {
      const response = await jobService.isJobSaved(id);
      setIsSaved(response.data.isSaved);
    } catch (error) {
      console.error('Error checking if job is saved:', error);
    }
  };

  const checkIfApplied = async () => {
    try {
      const response = await applicationService.checkIfApplied(id);
      setIsApplied(response.data?.hasApplied || false);
    } catch (error) {
      console.error('Error checking application status:', error);
    }
  };

  const handleSaveJob = async () => {
    if (!isAuthenticated) {
      toast.info('Please login to save jobs');
      navigate('/login');
      return;
    }

    if (user.role !== 'candidate') {
      toast.error('Only candidates can save jobs');
      return;
    }

    try {
      setSavingJob(true);
      if (isSaved) {
        await jobService.unsaveJob(id);
        setIsSaved(false);
        toast.success('Job removed from saved');
      } else {
        await jobService.saveJob(id);
        setIsSaved(true);
        toast.success('Job saved successfully');
      }
    } catch (error) {
      console.error('Error saving job:', error);
      toast.error(error.response?.data?.message || 'Failed to save job');
    } finally {
      setSavingJob(false);
    }
  };

  const formatSalary = (salary) => {
    if (!salary || !salary.min) return 'Not disclosed';
    const { min, max, currency, period } = salary;
    if (max) {
      return `${currency} ${min.toLocaleString()} - ${max.toLocaleString()} / ${period}`;
    }
    return `${currency} ${min.toLocaleString()} / ${period}`;
  };

  const formatLocation = (loc) => {
    if (!loc) return '';
    const city = typeof loc.city === 'object' ? loc.city?.name : loc.city;
    return [city, loc.state, loc.country, loc.address].filter(Boolean).join(', ');
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - new Date(date)) / 1000);
    const intervals = {
      year: 31536000,
      month: 2592000,
      week: 604800,
      day: 86400,
      hour: 3600,
      minute: 60
    };

    for (const [unit, secondsInUnit] of Object.entries(intervals)) {
      const interval = Math.floor(seconds / secondsInUnit);
      if (interval >= 1) {
        return `${interval} ${unit}${interval > 1 ? 's' : ''} ago`;
      }
    }
    return 'Just now';
  };

  if (loading) {
    return (
      <div className="public-job-detail-loading">
        <div className="jd-spinner"></div>
        <p>Loading job details...</p>
      </div>
    );
  }

  if (!job) {
    return (
      <div className={`public-job-detail${inDashboard ? ' in-dashboard' : ''}`}>
        <div className="jd-empty">
          <span className="material-symbols-outlined">work_off</span>
          <h3>Job not found</h3>
          <p>This job may have been removed or expired.</p>
          <Link to="/jobs" className="jd-empty-link">
            <span className="material-symbols-outlined">search</span>
            Browse all jobs
          </Link>
        </div>
      </div>
    );
  }

  const jobLocation = formatLocation(job.location);

  return (
    <div className={`public-job-detail${inDashboard ? ' in-dashboard' : ''}`}>
      <Link to="/jobs" className="jd-back-link">
        <span className="material-symbols-outlined">arrow_back</span>
        Back to Jobs
      </Link>

      {/* Hero */}
      <div className="jd-hero">
        <div className="jd-hero-left">
          <div className="jd-company-logo">
            {job.company?.logo
              ? <img src={job.company.logo} alt={job.company.name} />
              : (job.title?.charAt(0) || 'C')}
          </div>
          <div className="jd-title-wrap">
            <h1>{job.title}</h1>
            <p className="jd-company-name">{job.company?.name}</p>
            <div className="jd-hero-meta">
              {jobLocation && (
                <span className="jd-hero-meta-item">
                  <span className="material-symbols-outlined">location_on</span>
                  {jobLocation}
                </span>
              )}
              <span className="jd-hero-meta-item">
                <span className="material-symbols-outlined">work</span>
                {job.jobType?.name}
              </span>
              <span className="jd-hero-meta-item">
                <span className="material-symbols-outlined">business_center</span>
                {job.workMode}
              </span>
              <span className="jd-hero-meta-item">
                <span className="material-symbols-outlined">schedule</span>
                Posted {getTimeAgo(job.createdAt)}
              </span>
            </div>
          </div>
        </div>
        <div className="jd-hero-actions">
          {isApplied ? (
            <div className="jd-applied-badge">
              <span className="material-symbols-outlined">check_circle</span>
              Already Applied
            </div>
          ) : (
            <button className="jd-apply-btn" onClick={handleApply}>
              <span className="material-symbols-outlined">send</span>
              Apply Now
            </button>
          )}
          <button
            className={`jd-save-btn${isSaved ? ' saved' : ''}`}
            onClick={handleSaveJob}
            disabled={savingJob}
          >
            <span className="material-symbols-outlined">{isSaved ? 'bookmark' : 'bookmark_border'}</span>
            {savingJob ? 'Saving…' : isSaved ? 'Saved' : 'Save'}
          </button>
        </div>
      </div>

      {/* Main layout */}
      <div className="jd-layout">
        <div className="jd-main">
          {job.description && (
            <div className="jd-section">
              <h2 className="jd-section-title">
                <span className="material-symbols-outlined">description</span>
                Job Description
              </h2>
              <p className="jd-content-text">{job.description}</p>
            </div>
          )}

          {/* {job.responsibilities && (
            <div className="jd-section">
              <h2 className="jd-section-title">
                <span className="material-symbols-outlined">checklist</span>
                Responsibilities
              </h2>
              <p className="jd-content-text">{job.responsibilities}</p>
            </div>
          )} */}

          {job.requirements && (
            <div className="jd-section">
              <h2 className="jd-section-title">
                <span className="material-symbols-outlined">fact_check</span>
                Requirements
              </h2>
              <p className="jd-content-text">{job.requirements}</p>
            </div>
          )}

          {job.skills && job.skills.length > 0 && (
            <div className="jd-section">
              <h2 className="jd-section-title">
                <span className="material-symbols-outlined">psychology</span>
                Required Skills
              </h2>
              <div className="jd-skills">
                {job.skills.map((skill, i) => (
                  <span key={i} className="jd-skill">{skill.name}</span>
                ))}
              </div>
            </div>
          )}

          {job.benefits && job.benefits.length > 0 && (
            <div className="jd-section">
              <h2 className="jd-section-title">
                <span className="material-symbols-outlined">redeem</span>
                Benefits
              </h2>
              <div className="jd-skills">
                {job.benefits.map((b) => (
                  <span key={b._id} className="jd-skill">{b.name}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="jd-sidebar">
          <div className="jd-sidebar-card">
            <p className="jd-sidebar-title">Job Overview</p>
            <div className="jd-overview-list">
              <div className="jd-overview-item">
                <div className="jd-overview-icon"><span className="material-symbols-outlined">currency_rupee</span></div>
                <div><span className="jd-overview-label">Salary</span><span className="jd-overview-value">{formatSalary(job.salary)}</span></div>
              </div>
              {jobLocation && (
                <div className="jd-overview-item">
                  <div className="jd-overview-icon"><span className="material-symbols-outlined">location_on</span></div>
                  <div><span className="jd-overview-label">Location</span><span className="jd-overview-value">{jobLocation}</span></div>
                </div>
              )}
              {
                job.jobType?.name && (
                  <div className="jd-overview-item">
                    <div className="jd-overview-icon"><span className="material-symbols-outlined">work</span></div>
                    <div><span className="jd-overview-label">Job Type</span><span className="jd-overview-value">{job.jobType?.name}</span></div>
                  </div>
                )
              }
              {
                job.workMode && (
                  <div className="jd-overview-item">
                    <div className="jd-overview-icon"><span className="material-symbols-outlined">business_center</span></div>
                    <div><span className="jd-overview-label">Work Mode</span><span className="jd-overview-value">{job.workMode}</span></div>
                  </div>
                )
              }
              {job.experience && (
                <div className="jd-overview-item">
                  <div className="jd-overview-icon"><span className="material-symbols-outlined">trending_up</span></div>
                  <div><span className="jd-overview-label">Experience</span><span className="jd-overview-value">{job.experience.min}–{job.experience.max || '+'} yrs</span></div>
                </div>
              )}
              {job.education && (
                <div className="jd-overview-item">
                  <div className="jd-overview-icon"><span className="material-symbols-outlined">school</span></div>
                  <div><span className="jd-overview-label">Education</span><span className="jd-overview-value">{job.education.name}</span></div>
                </div>
              )}
              {/* <div className="jd-overview-item">
                <div className="jd-overview-icon"><span className="material-symbols-outlined">group</span></div>
                <div><span className="jd-overview-label">Applicants</span><span className="jd-overview-value">{job.applications || 0}</span></div>
              </div> */}
            </div>
          </div>

          <div className="jd-sidebar-card">
            <p className="jd-sidebar-title">About the Company</p>
            <p className="jd-company-desc">{job.company?.description || 'No description available.'}</p>
            {job.company?.website && (
              <a href={job.company.website} target="_blank" rel="noopener noreferrer" className="jd-company-link">
                Visit Website <span className="material-symbols-outlined">open_in_new</span>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Similar Jobs */}
      {similarJobs.length > 0 && (
        <div className="jd-similar">
          <h2 className="jd-similar-title">Similar Jobs</h2>
          <div className="jd-similar-grid">
            {similarJobs.map((sj) => (
              <Link to={`/jobs/${sj._id}`} key={sj._id} className="jd-similar-card">
                <h4>{sj.title}</h4>
                <p>{sj.company?.name}</p>
                {formatLocation(sj.location) && (
                  <span className="jd-similar-loc">
                    <span className="material-symbols-outlined">location_on</span>
                    {formatLocation(sj.location)}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default JobDetails;
